import {Modal} from "antd";
import ClientTabHeading from "../clients/ClientTabHeading";
import { Calendar, Plus, RefreshCw, User } from "lucide-react";
import InterviewFeedbackPanel from "../../components/InterviewFeedbackPanel";
import React, { useEffect, useRef, useState } from "react";
import axios from "../../axios/AxiosInstance";
import { AddInterviewModal } from "../../components/reusableComponents/AddInterviewModal";
import { candidateName } from "../../utils/candidateName";
import { apiErrorMessage } from "../../utils/apiError";
import {
  clearInterviewCache,
  loadInterviews,
} from "../../services/interviewApi";

const entityId = (value) => String(value?._id || value || "");
export default function InterviewsSection({ candidate, client }) {
  const [rows, setRows] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [selected, setSelected] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [candidateLoading, setCandidateLoading] = useState(false);
  const [createError, setCreateError] = useState("");
  const [revision, setRevision] = useState(0);
  const candidateRequest = useRef(null);
  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    async function load() {
      setError("");
      try {
        const results = await loadInterviews({
          candidateId: candidate?._id,
          clientId: client?._id,
          signal: controller.signal,
        });
        if (active && !controller.signal.aborted) {
          setRows(
            results
              .map((row) => ({
                ...row,
                person: row.candidate || candidate,
              }))
              .filter(
                (row) => !client || entityId(row.client) === entityId(client),
              ),
          );
        }
      } catch (error) {
        const detail = apiErrorMessage(error, "Impossible de charger les entretiens. Réessayez.");
        if (active && detail) setError(detail);
      } finally {
        if (active && !controller.signal.aborted) setLoading(false);
      }
    }
    load();
    return () => {
      active = false;
      controller.abort();
    };
  }, [candidate, client, revision]);

  useEffect(
    () => () => {
      candidateRequest.current?.abort();
    },
    [],
  );

  useEffect(()=>{
    const refreshFeedback=()=>{if(document.visibilityState==='hidden')return;clearInterviewCache();setRevision(value=>value+1);};
    window.addEventListener('focus',refreshFeedback);const timer=setInterval(refreshFeedback,60000);
    return()=>{window.removeEventListener('focus',refreshFeedback);clearInterval(timer);};
  },[]);

  const refresh = () => {
    clearInterviewCache();
    setRevision((value) => value + 1);
  };

  const openCreate = async () => {
    setCreateError("");
    if (candidate || candidates.length) {
      setOpen(true);
      return;
    }
    const controller = new AbortController();
    candidateRequest.current?.abort();
    candidateRequest.current = controller;
    setCandidateLoading(true);
    try {
      const { data } = await axios.get("tempworker-candidate/freelance", {
        signal: controller.signal,
      });
      if (candidateRequest.current !== controller) return;
      setCandidates(Array.isArray(data) ? data : []);
      setOpen(true);
    } catch (requestError) {
      const detail = apiErrorMessage(
        requestError,
        "Impossible de charger les candidats.",
      );
      if (candidateRequest.current === controller && detail) {
        setCreateError(detail);
      }
    } finally {
      if (candidateRequest.current === controller) {
        candidateRequest.current = null;
        setCandidateLoading(false);
      }
    }
  };

  const person =
    candidate ||
    candidates.find((row) => entityId(row) === String(selected));
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        {client ? <ClientTabHeading title="Entretiens" description="Planifiez les entretiens des candidats avec ce client et consultez les retours." /> : (
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Entretiens</h2>
          <p className="mt-1 text-sm text-slate-500">
            Rendez-vous et informations partagés entre les fiches candidat et
            client.
          </p>
        </div>
        )}
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            disabled={loading}
            onClick={refresh}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            <RefreshCw size={16} />Actualiser
          </button>
          <button
            type="button"
            disabled={loading || Boolean(error) || candidateLoading}
            onClick={openCreate}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-700 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-800 disabled:opacity-50"
          >
            <Plus size={16} />
            {candidateLoading ? "Chargement…" : "Ajouter un entretien"}
          </button>
        </div>
      </div>
      {createError && <p role="alert">{createError}</p>}
      {loading ? (
        <p role="status">Chargement des entretiens…</p>
      ) : error ? (
        <div role="alert">
          {error}
          <button
            onClick={() => setRevision((value) => value + 1)}
            className="ml-3 text-blue-700"
          >
            Réessayer
          </button>
        </div>
      ) : !rows.length ? (
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-8 text-center"><Calendar size={28} className="mx-auto mb-3 text-blue-500" /><p className="text-sm font-medium text-slate-700">Aucun entretien programmé.</p><p className="mt-1 text-sm text-slate-500">Ajoutez un entretien pour organiser une rencontre avec un candidat.</p></div>
      ) : (
        rows
          .slice()
          .sort((a, b) =>
            `${b.date} ${b.hour}`.localeCompare(`${a.date} ${a.hour}`),
          )
          .map((row, index) => (
            <article
              key={row._id || index}
              className="rounded-xl border border-slate-200 bg-white p-3 sm:p-4"
            >
              <div className="flex items-center gap-3 border-b border-slate-100 pb-3"><span className="rounded-lg bg-blue-50 p-2 text-blue-700"><User size={18} /></span><h3 className="text-base font-semibold text-slate-900">{candidateName(row.person)}</h3></div>
              <dl className="mt-2 grid gap-x-5 gap-y-2 text-sm sm:grid-cols-2 lg:grid-cols-3">
                {[
                  [
                    "Client concerné",
                    row.client?.companyName ||
                      row.client?.denomination ||
                      (entityId(row.client) === entityId(client)
                        ? client?.companyName || client?.denomination
                        : "") ||
                      row.clientName ||
                      "Non renseigné",
                  ],
                  [
                    "Date",
                    row.date && !Number.isNaN(Date.parse(String(row.date)))
                      ? new Date(
                          `${String(row.date).slice(0, 10)}T12:00:00`,
                        ).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" })
                      : "Non renseignée",
                  ],
                  ["Heure", row.hour || row.time || "Non renseignée"],
                  ["Type d’entretien", row.type || "Non renseigné"],
                  ["Poste", row.poste || "Non renseigné"],
                  ["Lieu", row.place || "Non renseigné"],
                ].filter(([label]) => !client || label !== "Client concerné").map(([label, value]) => (
                  <div key={label} className="min-w-0 rounded-lg bg-slate-50 p-3">
                    <dt className="shrink-0 text-xs font-medium text-slate-500">
                      {label}
                    </dt>
                    <dd className="mt-1 min-w-0 whitespace-pre-wrap break-words font-medium text-slate-900">
                      {value}
                    </dd>
                  </div>
                ))}
              </dl>
              {row.comment && (
                <p className="mt-2 whitespace-pre-wrap break-words text-sm text-slate-700">
                  <span className="mr-2 text-xs font-medium text-slate-500">
                    Notes complémentaires
                  </span>
                  {row.comment}
                </p>
              )}
              <InterviewFeedbackPanel interview={row} onUpdated={updated=>{clearInterviewCache();setRows(current=>current.map(item=>item._id===updated._id?updated:item));}} />
            </article>
          ))
      )}
      <Modal open={open&&!person} centered width={560} zIndex={2200} title="Planifier un entretien" footer={null} onCancel={()=>setOpen(false)}>
        <div className="mt-4 space-y-4"><p className="text-sm text-slate-500">Choisissez le candidat à présenter à {client?.companyName||'ce client'}.</p><label htmlFor="interview-candidate" className="block text-sm font-medium text-slate-700">Candidat / intérimaire</label><select id="interview-candidate" value={selected} onChange={event=>setSelected(event.target.value)} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-3 text-sm"><option value="">Choisir un candidat</option>{candidates.map(row=><option key={row._id} value={row._id}>{candidateName(row)}</option>)}</select><div className="flex justify-end border-t border-slate-100 pt-4"><button type="button" onClick={()=>setOpen(false)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600">Annuler</button></div></div>
      </Modal>
      {person && (
        <AddInterviewModal
          isOpen={open}
          candidate={person}
          initialClient={client}
          onClose={() => {
            setOpen(false);
            setSelected("");
          }}
          onSubmit={() => {
            clearInterviewCache();
            setRevision((value) => value + 1);
          }}
        />
      )}
    </div>
  );
}
